'use client';

import React, { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

interface AppLayoutProps {
  children: React.ReactNode;
}

const TESORERO_ALLOWED_PATHS = ['/finance-collections', '/budget'];

export default function AppLayout({ children }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, loading, userRole } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!loading && !user) {
      router.replace('/sign-up-login-screen');
      return;
    }
    if (!loading && user && userRole === 'tesorero') {
      const isAllowed = TESORERO_ALLOWED_PATHS.some((p) => pathname.startsWith(p));
      if (!isAllowed) {
        router.replace('/finance-collections');
      }
    }
  }, [user, loading, userRole, pathname, router]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const sidebarWidth = collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)';

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Mobile hamburger button — fixed, always visible on small screens */}
      <button
        className="md:hidden fixed top-0 left-0 z-[35] flex items-center justify-center bg-primary text-white"
        style={{ width: 'var(--topbar-height)', height: 'var(--topbar-height)' }}
        onClick={() => setMobileOpen(true)}
        aria-label="Abrir menú"
      >
        <Icon name="Bars3Icon" size={22} variant="outline" />
      </button>

      {/* Main content — no left margin on mobile, sidebar margin on desktop */}
      <div
        className="flex-1 flex flex-col transition-all duration-300 ease-in-out md:ml-[var(--sidebar-width)]"
        style={{ marginLeft: 0 }}
      >
        <style>{`
          @media (min-width: 768px) {
            .app-content-wrapper {
              margin-left: ${sidebarWidth} !important;
            }
          }
        `}</style>
        <div className="app-content-wrapper flex-1 flex flex-col transition-all duration-300 ease-in-out">
          <Topbar sidebarCollapsed={collapsed} />
          <main
            className="flex-1 overflow-auto"
            style={{ marginTop: 'var(--topbar-height)' }}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}